import React, { useEffect, useMemo, useState } from "react";
import { formatNumber, formatPercent } from "../utils/formatters";

function monthLabel(monthKey) {
  const [yearRaw, monthRaw] = String(monthKey || "").split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthKey || "-";
  try {
    const label = new Intl.DateTimeFormat("pt-BR", {
      month: "long",
      year: "numeric",
    }).format(new Date(year, month - 1, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return monthKey;
  }
}

export default function MonthlyMachineCapacityPanel({ monthlyMachineCapacity }) {
  const months = monthlyMachineCapacity?.availableMonths || [];
  const [selectedMonth, setSelectedMonth] = useState(months[0] || "");
  const [selectedMachineId, setSelectedMachineId] = useState("");

  useEffect(() => {
    if (!months.length) {
      setSelectedMonth("");
      return;
    }
    if (!months.includes(selectedMonth)) {
      setSelectedMonth(months[0]);
    }
  }, [months, selectedMonth]);

  const currentMonthData = monthlyMachineCapacity?.byMonth?.[selectedMonth] || null;

  const topRows = useMemo(
    () => (currentMonthData?.machineRows || []).slice().sort((a, b) => b.monthlyCapacity - a.monthlyCapacity),
    [currentMonthData]
  );

  const monthlyTotalsRows = useMemo(
    () =>
      months.map((monthKey) => {
        const monthData = monthlyMachineCapacity?.byMonth?.[monthKey];
        return {
          monthKey,
          totals: monthData?.totals || {
            contractHoursTotal: 0,
            grossCapacity: 0,
            safetyMargin: 0,
            monthlyCapacity: 0,
          },
        };
      }),
    [months, monthlyMachineCapacity]
  );

  const yearlyTotals = useMemo(
    () =>
      monthlyTotalsRows.reduce(
        (acc, row) => ({
          contractHoursTotal: acc.contractHoursTotal + row.totals.contractHoursTotal,
          grossCapacity: acc.grossCapacity + row.totals.grossCapacity,
          safetyMargin: acc.safetyMargin + row.totals.safetyMargin,
          monthlyCapacity: acc.monthlyCapacity + row.totals.monthlyCapacity,
        }),
        { contractHoursTotal: 0, grossCapacity: 0, safetyMargin: 0, monthlyCapacity: 0 }
      ),
    [monthlyTotalsRows]
  );

  const machineOptions = useMemo(() => {
    const firstMonthWithRows = months.find(
      (monthKey) => (monthlyMachineCapacity?.byMonth?.[monthKey]?.machineRows || []).length
    );
    return (monthlyMachineCapacity?.byMonth?.[firstMonthWithRows]?.machineRows || []).map((row) => ({
      id: row.machineId,
      label: `${row.machinePrefix} (${row.machineCode})`,
    }));
  }, [months, monthlyMachineCapacity]);

  useEffect(() => {
    if (!machineOptions.length) {
      setSelectedMachineId("");
      return;
    }
    if (!machineOptions.some((item) => item.id === selectedMachineId)) {
      setSelectedMachineId(machineOptions[0].id);
    }
  }, [machineOptions, selectedMachineId]);

  const monthlyRowsForSelectedMachine = useMemo(
    () =>
      months.map((monthKey) => {
        const monthData = monthlyMachineCapacity?.byMonth?.[monthKey];
        const machineRow =
          (monthData?.machineRows || []).find((row) => row.machineId === selectedMachineId) || null;
        return {
          monthKey,
          row: machineRow,
        };
      }),
    [months, monthlyMachineCapacity, selectedMachineId]
  );

  return (
    <section className="swb-panel">
      <div className="swb-panel__head">
        <h3>Capacidade mensal (bases)</h3>
        <span className="swb-subtle">Calendarios + contrato + produtividade + aproveitamento</span>
      </div>

      <div className="swb-fields-grid">
        <label className="swb-field swb-field--full">
          <span>Mes de referencia</span>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {months.map((monthKey) => (
              <option key={monthKey} value={monthKey}>
                {monthLabel(monthKey)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {machineOptions.length ? (
        <>
          <div className="swb-panel__head" style={{ marginTop: 10 }}>
            <h4>Dividido por mes (por maquina)</h4>
            <span className="swb-subtle">Capacidade mensal da maquina selecionada</span>
          </div>

          <div className="swb-fields-grid" style={{ marginTop: 8 }}>
            <label className="swb-field swb-field--full">
              <span>Maquina</span>
              <select value={selectedMachineId} onChange={(e) => setSelectedMachineId(e.target.value)}>
                {machineOptions.map((machine) => (
                  <option key={machine.id} value={machine.id}>
                    {machine.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="swb-list" style={{ marginTop: 8 }}>
            {monthlyRowsForSelectedMachine.map((entry) => {
              const isActive = entry.monthKey === selectedMonth;
              const row = entry.row;
              return (
                <button
                  key={`machine_month_${selectedMachineId}_${entry.monthKey}`}
                  type="button"
                  className={`swb-list-item ${isActive ? "is-active" : ""}`}
                  onClick={() => setSelectedMonth(entry.monthKey)}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  <div className="swb-list-item__content">
                    <strong>{monthLabel(entry.monthKey)}</strong>
                    <span>
                      Dias operador: {formatNumber(row?.daysWithOperator, 0)} · Dias sem manut.:{" "}
                      {formatNumber(row?.daysWithoutMaintenance, 0)} · Horas:{" "}
                      {formatNumber(row?.contractHoursTotal, 0)}
                    </span>
                  </div>
                  <div className="swb-list-item__actions">
                    <strong>{formatNumber(row?.monthlyCapacity, 0)}</strong>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {monthlyTotalsRows.length ? (
        <>
          <div className="swb-panel__head" style={{ marginTop: 10 }}>
            <h4>Totais por mes</h4>
            <span className="swb-subtle">{monthlyTotalsRows.length} mes(es)</span>
          </div>

          <div className="swb-list" style={{ marginTop: 8 }}>
            {monthlyTotalsRows.map((row) => {
              const isActive = row.monthKey === selectedMonth;
              return (
                <button
                  key={`month_total_${row.monthKey}`}
                  type="button"
                  className={`swb-list-item ${isActive ? "is-active" : ""}`}
                  onClick={() => setSelectedMonth(row.monthKey)}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  <div className="swb-list-item__content">
                    <strong>{monthLabel(row.monthKey)}</strong>
                    <span>
                      Horas: {formatNumber(row.totals.contractHoursTotal, 0)} · Bruta:{" "}
                      {formatNumber(row.totals.grossCapacity, 0)} · Margem:{" "}
                      {formatNumber(row.totals.safetyMargin, 0)}
                    </span>
                  </div>
                  <div className="swb-list-item__actions">
                    <strong>{formatNumber(row.totals.monthlyCapacity, 0)}</strong>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="swb-summary-grid" style={{ marginTop: 10 }}>
            <div className="swb-summary-item">
              <span>Total anual (horas)</span>
              <strong>{formatNumber(yearlyTotals.contractHoursTotal, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Total anual bruto</span>
              <strong>{formatNumber(yearlyTotals.grossCapacity, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Total anual margem</span>
              <strong>{formatNumber(yearlyTotals.safetyMargin, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Total anual capacidade</span>
              <strong>{formatNumber(yearlyTotals.monthlyCapacity, 0)}</strong>
            </div>
          </div>
        </>
      ) : null}

      {currentMonthData ? (
        <>
          <div className="swb-panel__head" style={{ marginTop: 14 }}>
            <h4>Detalhe do mes selecionado</h4>
            <span className="swb-subtle">{monthLabel(selectedMonth)}</span>
          </div>

          <div className="swb-summary-grid" style={{ marginTop: 10 }}>
            <div className="swb-summary-item">
              <span>Horas contrato (total)</span>
              <strong>{formatNumber(currentMonthData.totals.contractHoursTotal, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Capacidade bruta</span>
              <strong>{formatNumber(currentMonthData.totals.grossCapacity, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Margem aplicada</span>
              <strong>{formatNumber(currentMonthData.totals.safetyMargin, 0)}</strong>
            </div>
            <div className="swb-summary-item">
              <span>Capacidade mensal</span>
              <strong>{formatNumber(currentMonthData.totals.monthlyCapacity, 0)}</strong>
            </div>
          </div>

          <p className="swb-subtle" style={{ marginTop: 10 }}>
            Formula aplicada por dia: horas de contrato x produtividade x fator operacional (0x/1x/2x),
            bloqueando dias de manutencao. No fim aplica a taxa de aproveitamento (ex.: 90%) e reserva a margem (10%).
          </p>

          <div className="swb-list" style={{ marginTop: 10 }}>
            {topRows.map((row) => (
              <div key={`${selectedMonth}_${row.machineId}`} className="swb-list-item">
                <div className="swb-list-item__content">
                  <strong>
                    {row.machinePrefix} ({row.machineCode})
                  </strong>
                  <span>
                    Dias operador: {formatNumber(row.daysWithOperator, 0)} · Dias sem manut.:{" "}
                    {formatNumber(row.daysWithoutMaintenance, 0)} · Horas contrato:{" "}
                    {formatNumber(row.contractHoursTotal, 0)}
                  </span>
                  <span>
                    Produtividade/h: {formatNumber(row.productivityPerHour, 0)} · Aproveitamento:{" "}
                    {formatPercent(row.utilizationPct, 0)} · Margem: {formatNumber(row.safetyMargin, 0)}
                  </span>
                </div>
                <div className="swb-list-item__actions">
                  <strong>{formatNumber(row.monthlyCapacity, 0)}</strong>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="swb-empty">Sem dados de calendario para calcular capacidade mensal.</div>
      )}
    </section>
  );
}
